import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ParrotBrandMark from '../components/ParrotBrandMark';

const APP_STORE_URL =
  'https://apps.apple.com/us/app/parrot-speech-improvement/id1457391626';

type CheckoutStatus = {
  email?: string;
  subscriptionStatus?: string;
};

function statusCopy(status: string, email?: string) {
  const accountLabel = email ? email : 'the same email that received this offer';

  switch (status) {
    case 'already_active':
      return {
        eyebrow: 'You are already set',
        title: 'Your Parrot access is active.',
        body: `Download the Parrot app, sign in with ${accountLabel}, and you will be ready to use it.`,
      };
    case 'checkout_not_ready':
      return {
        eyebrow: 'Almost ready',
        title: 'Parrot checkout is being connected.',
        body: 'Your invite is ready, but Stripe still needs the final subscription price before checkout can open. Come back to this link once checkout is live.',
      };
    case 'expired_offer':
      return {
        eyebrow: 'Invite expired',
        title: 'This free-month link has expired.',
        body: 'Open the latest Parrot email and tap the newest offer link.',
      };
    case 'invalid_offer':
      return {
        eyebrow: 'Offer link issue',
        title: 'This invite link is incomplete.',
        body: 'Open the Parrot email again and tap the offer button from that message.',
      };
    case 'user_not_found':
      return {
        eyebrow: 'Account not found',
        title: 'We could not find the Parrot account for this invite.',
        body: 'Reply to the email and we can help attach the free month to the right account.',
      };
    case 'missing_email':
      return {
        eyebrow: 'Email needed',
        title: 'This account needs an email before checkout.',
        body: 'Reply to the email and we can help update the account before starting the free month.',
      };
    case 'checkout_error':
      return {
        eyebrow: 'Checkout paused',
        title: 'We could not open checkout.',
        body: 'Try the offer link again in a moment. If it keeps happening, reply to the Parrot email and we will help.',
      };
    default:
      return {
        eyebrow: 'Success',
        title: 'Your free month is active.',
        body: `Download the Parrot app, sign in with ${accountLabel}, and you will be ready to use it.`,
      };
  }
}

const SubscriptionSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status') || 'success';
  const sessionId = searchParams.get('session_id') || '';
  const emailFromUrl = searchParams.get('email') || '';
  const [checkoutStatus, setCheckoutStatus] = useState<CheckoutStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(Boolean(sessionId));

  useEffect(() => {
    if (!sessionId) return;
    let mounted = true;
    setLoadingStatus(true);
    fetch(`/api/get-parrot-checkout-status?session_id=${encodeURIComponent(sessionId)}`)
      .then((resp) => resp.json())
      .then((json) => {
        if (!mounted) return;
        setCheckoutStatus(json?.success ? json : null);
      })
      .catch(() => {
        if (mounted) setCheckoutStatus(null);
      })
      .finally(() => {
        if (mounted) setLoadingStatus(false);
      });
    return () => {
      mounted = false;
    };
  }, [sessionId]);

  const email = checkoutStatus?.email || emailFromUrl;
  const copy = useMemo(() => statusCopy(status, email), [status, email]);
  const isPositive = status === 'success' || status === 'already_active';

  return (
    <div className="min-h-screen bg-parrot-black text-white">
      <header className="flex items-center justify-between px-6 py-6 sm:px-12 lg:px-16">
        <Link to="/" className="flex items-center gap-3">
          <ParrotBrandMark className="h-11 w-11" />
          <span className="text-sm font-black tracking-[0.28em] text-parrot-yellow">PARROT</span>
        </Link>
        <Link to="/help" className="text-sm font-bold text-white/70 hover:text-parrot-yellow">
          Help
        </Link>
      </header>

      <main className="px-6 pb-16 pt-10 sm:px-12 lg:px-16">
        <section className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-[0.95fr_1.05fr]">
          <div className="order-2 lg:order-1">
            <div className="inline-flex rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-[0.22em] text-parrot-yellow">
              {copy.eyebrow}
            </div>
            <h1 className="mt-6 text-5xl font-black leading-[0.95] tracking-normal sm:text-7xl">
              {copy.title}
            </h1>
            <p className="mt-6 max-w-2xl text-xl font-semibold leading-8 text-white/75">
              {loadingStatus && isPositive
                ? 'Confirming the checkout details for this account...'
                : copy.body}
            </p>

            {email && isPositive ? (
              <div className="mt-6 rounded-2xl border border-parrot-yellow/40 bg-parrot-yellow px-5 py-4 text-parrot-black">
                <div className="text-xs font-black uppercase tracking-[0.2em]">Account email</div>
                <div className="mt-1 break-words text-2xl font-black">{email}</div>
              </div>
            ) : null}

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center rounded-full bg-parrot-yellow px-7 py-4 text-base font-black text-parrot-black transition-transform hover:-translate-y-0.5"
              >
                Download Parrot
              </a>
              <Link
                to="/help"
                className="inline-flex items-center justify-center rounded-full border border-white/20 bg-white/10 px-7 py-4 text-base font-black text-white transition-transform hover:-translate-y-0.5"
              >
                Get help
              </Link>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <div className="relative mx-auto aspect-square max-w-[560px]">
              <div className="absolute inset-8 rounded-full bg-parrot-blue" />
              <div className="absolute inset-16 rounded-full bg-parrot-coral" />
              <div className="absolute inset-24 rounded-full bg-parrot-yellow" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="rounded-[2rem] border-4 border-parrot-black bg-white p-8 text-center text-parrot-black shadow-[12px_12px_0_#FFD43B]">
                  <div className="text-7xl font-black">{isPositive ? 'Success!' : 'Parrot'}</div>
                  <div className="mt-4 text-lg font-black text-parrot-blue">
                    {isPositive ? 'Free month activated' : 'Offer status'}
                  </div>
                  <div className="mx-auto mt-6 h-4 w-48 rounded-full bg-parrot-black/10">
                    <div className="h-4 w-full rounded-full bg-parrot-coral" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default SubscriptionSuccess;
